/* eslint-disable */
import {
  LinkOutlined,
  QrcodeOutlined
} from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button, Card, Col,
  message,
  Row,
  Space, Spin,
  Switch,
  Typography
} from 'antd';
import { AxiosError } from 'axios';
import React, { useMemo, useState } from 'react';
import {
  FormProvider,
  useForm
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  useMutation, useQuery
} from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import ApprovedStaffs from './approvedStaffs';
import QrCodeModal from './qrCodeModal';
import WaitForApproveStaffs from './waitForApproveStaffs';

import { useAppSelector } from 'app/store';
import HeaderPage from 'common/components/HeaderPage';
import {
  getPayrallFromProjectService,
  getPermissionFromProjectByIdService,
  updateLinkOpenService
} from 'common/services/extends/project';
import { detectError, formatDateDDMMYYYY, handleCopy } from 'common/utils/functions';
import { projectSchema } from 'common/utils/schemas';
import roles, { getPermission } from 'configs/roles';
import TimekeepingDetail from 'extends/ApproveSalary/Timekeeping';
import { getPayrollPeriodsByIdService } from 'common/services/extends/payrollPeriods';
import { ROUTE_PATHS } from 'common/utils/constant';

interface ProjectForm {
  name: string;
  code: string;
  rangeDate: [string, string];
  account?: OptionType;
  operator?: OptionType;
  payroll: {
    id: number,
    fullname: string
    code: string
    idCardNumber: string
    isLinkOpened: boolean
  };
}

const defaultValues = {
  name: '',
  code: '',
  account: undefined,
  operator: undefined,
  payrolls: [
    {
      option: undefined,
      code: ''
    }
  ],
};

const TableSalaryDetail: React.FC<ActiveRoles> = ({ roleIndex }) => {
  /* Hooks */
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const projectId = searchParams.get('projectId') || '';
  const staffFullTimeId = searchParams.get('staffFullTimeId') || '';
  const payrollPeriodId = searchParams.get('payrollPeriodId') || '';

  /* States */
  const [qrCode, setQrCode] = useState('');
  const [isOpenLink, setIsOpenLink] = useState(false);
  const rolesUser = useAppSelector((state) => state.auth.roles);

  /* React-hook-form */
  const method = useForm<ProjectForm>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(projectSchema),
  });

  const queryKey = ['salary-from-project-detail', projectId, staffFullTimeId];

  const { data: projectData, isLoading } = useQuery(
    queryKey,
    () => {
      if (projectId && staffFullTimeId) {
        return getPayrallFromProjectService(projectId, staffFullTimeId)
      }
      return null;
    },
    {
      enabled: !!(projectId && staffFullTimeId) && roleIndex,
      onSuccess: (data) => {
        if (data) {
          method.reset({
            name: data.projectData?.name || '',
            code: data.projectData?.code || '',
            rangeDate: [data.projectData?.fromDate, data.projectData?.toDate],
            account: data.accountant ? {
              label: data.accountant.fullname,
              value: data.accountant.id,
            } : undefined,
            operator: data.operation ? {
              label: data.operation.fullname,
              value: data.operation.id,
            } : undefined,
            payroll: data.payrollData
          });
        }
      },
      onError: (error: unknown) => {
        if (error instanceof AxiosError) {
          const status = error.response?.status;
          message.error(t(detectError(status)));
        }
      }
    }
  );

  const { isLoading: isLoadingPermission, data: permission } = useQuery(
    ['project-permission-detail', projectId, staffFullTimeId],
    () => getPermissionFromProjectByIdService(
      projectId,
      {
        staffFullTimeId
      }
    ),
    {
      enabled: !!staffFullTimeId && !!projectId,
    }
  );

  const {
    mutate: updateLinkOpenMutate,
    isLoading: updateLinkOpenLoading
  } = useMutation(
    ['update-status-project'],
    async (params: { isLinkOpened: boolean }) => updateLinkOpenService(payrollPeriodId, params),
    {
      onSuccess: (_, variables) => {
        setIsOpenLink(variables.isLinkOpened);
        message.success(t('message.updateSuccess'));
      },
      onError: (error: unknown) => {
        if (error instanceof AxiosError) {
          const status = error.response?.status;
          message.error(t(detectError(status)));
        }
      }
    }
  );

  const { data: payrollData } = useQuery(
    ['payroll-periods-detail', payrollPeriodId],
    () => getPayrollPeriodsByIdService(payrollPeriodId),
    {
      enabled: !!payrollPeriodId && getPermission(rolesUser, roles.PAYROLL_PERIOD_INDEX),
      onSuccess: (data) => {
        setIsOpenLink(data.payrollPeriodData?.isLinkOpened || false);
      }
    }
  );

  const salaryIsCalculated = useMemo(() => payrollData?.payrollPeriodData?.statusApprove === 2 || payrollData?.payrollPeriodData?.statusApprove === 3, [payrollData]);

  /* Render */
  return (
    <div className="p-TableSalaryDetail">
      <HeaderPage
        fixed
        title={t('sidebar.program')}
        rightHeader={(
          <Button
            onClick={() => navigate(`${ROUTE_PATHS.TIMEKEEPING_DETAIL}?projectId=${projectId}&staffFullTimeId=${staffFullTimeId}&payrollPeriodId=${payrollPeriodId}`)}
          >
            Chấm công
          </Button>
        )}
      />
      <Spin size="large" spinning={isLoading || isLoadingPermission}>
        <div className="t-mainlayout_wrapper">
          <FormProvider {...method}>
            <Row gutter={16}>
              <Col>
                <Space direction="vertical" size={24} style={{ width: '100%' }}>
                  <Card
                    title={(
                      <Space size={0} style={{ width: '100%', justifyContent: 'space-between' }}>
                        <Typography.Title level={4}>
                          {t('program.info')}
                        </Typography.Title>
                        <Space>
                          <Button onClick={() => setQrCode(`${payrollData?.payrollPeriodData?.websiteDomain}/${payrollData?.payrollPeriodData?.code}`)}>
                            <QrcodeOutlined />
                            {t('program.downloadQrCode')}
                          </Button>
                          <Button
                            onClick={() => handleCopy({
                              data: `${payrollData?.payrollPeriodData?.websiteDomain}/${payrollData?.payrollPeriodData?.code}`,
                              messageFailed: t('message.failedCopied'),
                              messageSuccess: t('message.successcopied')
                            })}
                          >
                            <LinkOutlined />
                            {t('program.copyLink')}
                          </Button>
                          {
                            getPermission(rolesUser, roles.PROJECT_UPDATE_LINK) && (
                              <Space>
                                <Switch
                                  loading={updateLinkOpenLoading}
                                  checked={isOpenLink}
                                  onChange={(value) => {
                                    updateLinkOpenMutate({
                                      isLinkOpened: value
                                    });
                                  }}
                                  disabled={!permission?.canDo || salaryIsCalculated}
                                />
                                <Typography.Text>
                                  {t('program.isActive')}
                                </Typography.Text>
                              </Space>
                            )
                          }
                        </Space>
                      </Space>
                    )}
                  >
                    <div className="site-card-border-less-wrapper">
                      <Space
                        direction="vertical"
                        size={12}
                        style={{ width: '100%' }}
                      >
                        <Row gutter={16}>
                          <Col sm={12} xl={6} className="u-mb-8">
                            <Typography.Text>
                              {t('program.code')}
                              {': '}
                            </Typography.Text>
                            <div>
                              <Typography.Text strong>
                                {projectData?.projectData.code}
                              </Typography.Text>
                            </div>
                          </Col>
                          <Col sm={12} xl={6} className="u-mb-8">
                            <Typography.Text>
                              {t('program.nameCustomer')}
                              {': '}
                            </Typography.Text>
                            <div>
                              <Typography.Text strong>
                                {projectData?.projectData?.customerName}
                              </Typography.Text>
                            </div>
                          </Col>
                          <Col sm={12} xl={6} className="u-mb-8">
                            <Typography.Text>
                              {t('program.name')}
                              {': '}
                            </Typography.Text>
                            <div>
                              <Typography.Text strong>
                                {projectData?.projectData.name}
                              </Typography.Text>
                            </div>
                          </Col>
                          <Col sm={12} xl={6} className="u-mb-8">
                            <Typography.Text>
                              {t('program.fromDate')}
                              {': '}
                            </Typography.Text>
                            <div>
                              <Typography.Text strong>
                                {formatDateDDMMYYYY(projectData?.projectData.fromDate, '/')}
                              </Typography.Text>
                            </div>
                          </Col>
                          <Col sm={12} xl={6} className="u-mb-8">
                            <Typography.Text>
                              {t('program.toDate')}
                              {': '}
                            </Typography.Text>
                            <div>
                              <Typography.Text strong>
                                {formatDateDDMMYYYY(projectData?.projectData.toDate, '/')}
                              </Typography.Text>
                            </div>
                          </Col>
                          <Col sm={12} xl={6} className="u-mb-8">
                            <Typography.Text>
                              {t('program.personInChargeAC')}
                              {': '}
                            </Typography.Text>
                            <div>
                              <Typography.Text strong>
                                {projectData?.accountant?.fullname || ''}
                              </Typography.Text>
                            </div>
                          </Col>
                          <Col sm={12} xl={6} className="u-mb-8">
                            <Typography.Text>
                              {t('program.personInChargeOP')}
                              {': '}
                            </Typography.Text>
                            <div>
                              <Typography.Text strong>
                                {projectData?.operation?.fullname || ''}
                              </Typography.Text>
                            </div>
                          </Col>
                          <Col sm={12} xl={6} className="u-mb-8">
                            <Typography.Text>
                              {t('program.creatorTableSalary')}
                              {': '}
                            </Typography.Text>
                            <div>
                              <Typography.Text strong>
                                {projectData?.payrollData.fullname || ''}
                              </Typography.Text>
                            </div>
                          </Col>
                        </Row>
                      </Space>
                    </div>
                  </Card>
                </Space>
              </Col>
            </Row>
            <Row gutter={16} className="u-mt-16">
              <Col>
                <TimekeepingDetail
                  endDate={payrollData?.payrollPeriodData?.endDate || ''}
                  fromDate={payrollData?.payrollPeriodData?.startDate || ''}
                  name={payrollData?.payrollPeriodData?.name || ''}
                />
              </Col>
            </Row>
            <Row gutter={16} className="u-mt-16" style={{ alignItems: 'stretch' }}>
              <Col span={12}>
                <WaitForApproveStaffs
                  disabledAction={!permission?.canDo || salaryIsCalculated}
                // disabledAction={projectData?.projectData.payrollCalculated}
                />
              </Col>
              <Col span={12}>
                <ApprovedStaffs
                  projectData={{
                    name: projectData?.projectData.name || '',
                    customerName: projectData?.projectData.customerName || '',
                    code: projectData?.projectData.code || '',
                    payrollName: projectData?.payrollData.fullname || ''
                  }}
                  isOpenLink={isOpenLink}
                  payrollData={payrollData}
                  disabledAction={!permission?.canDo || salaryIsCalculated}
                // disabledAction={projectData?.projectData.payrollCalculated}
                />
              </Col>
            </Row>
          </FormProvider>
        </div>
      </Spin>
      <QrCodeModal qrCode={qrCode} handleClose={() => setQrCode('')} />
    </div>
  );
};

export default TableSalaryDetail;
